import React, { useState } from 'react';
import type {
    CalendarAppointmentDto,
    CalendarStatusDto,
    CalendarStatusKey,
} from '../../../../types/api-responses';
import { AppointmentStatus } from '../../../../types/enums';
// AB#1901 (W-4) — Boundary-parse appointment.status so the calendar card
// renders the correct status colour + glyph regardless of whether the BE
// returns the int (legacy/cached) or the string ("Completed"/"Cancelled"/…)
// form. The previous switch-on-int fell through default for every string
// value → all Completed/Cancelled/NoShow appointments rendered with the
// Booked glyph (•) on the Calendar Day view.
import { parseAppointmentStatus } from '../../../../lib/enumParsers';
import { useLanguage } from '../../../../contexts/LanguageContext';
// O1 (AB#2199) — the booking detail (status words + owner actions) is the shared
// BookingDetailDialog, also used by the Appointments list.
import BookingDetailDialog, { type BookingDetailModel } from '../../../../components/BookingDetail/BookingDetailDialog';
// AB#1797 (W2, AC-8) — Payment badge + mark-paid modal on calendar card.
// Purely additive. Gated by REACT_APP_APPOINTMENTS_LIST_V2_ENABLED so the
// existing card is completely unaffected when the flag is OFF.
import PaymentBadge, { PaymentBadgeVariant } from '../../../../components/PaymentBadge/PaymentBadge';
import MarkAsPaidModal from '../../../../components/MarkAsPaidModal/MarkAsPaidModal';
import s from './AppointmentCard.module.css';

// AB#1797 (W2) — UAT FORCE-ON (CR-2026-05-30): was `process.env.REACT_APP_APPOINTMENTS_LIST_V2_ENABLED === 'true'`.
// Hardcoded for UAT so the Calendar Day-view card payment badge renders against the deployed bundle.
// REVERT once UAT sign-off is in — restore the env-flag check.
const APPOINTMENTS_LIST_V2 = true;

function generateIdempotencyKey(): string {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
}

/**
 * AB#1749 — Map the appointment status to the status palette key.
 *
 * AB#1901 (W-4) — Accept BOTH the BE wire string ("Pending"/"Completed"/…)
 * AND the legacy int form (1..7). Previously this function was typed
 * `status: number` and silently fell through default for every string
 * value, so all Completed / Cancelled / NoShow / Arrived / Started
 * appointments rendered with the Booked glyph (•) and the Booked colour
 * stripe on the Calendar Day view. The fix normalizes through
 * `parseAppointmentStatus` first.
 *
 * "Pending" is the FE-side label for what the calendar shows as
 * "Booked" — they're the same lifecycle state semantically.
 */
function statusToKey(status: number | string): CalendarStatusKey {
    const normalized = parseAppointmentStatus(status);
    switch (normalized) {
        case AppointmentStatus.Pending: return 'Booked';
        case AppointmentStatus.Confirmed: return 'Booked'; // BE "Approved" → FE Confirmed → Booked
        case AppointmentStatus.Completed: return 'Completed';
        case AppointmentStatus.Cancelled: return 'Cancelled';
        case AppointmentStatus.NoShow: return 'NoShow';
        case 6: return 'Arrived';   // post-C4 extension
        case 7: return 'Started';   // post-C4 extension
        default:
            // AB#1901 — Log unknown statuses for diagnostics rather than
            // silently falling back to 'Booked'. The neutral fallback below
            // keeps the card visible at the bottom of the palette so the
            // owner can still interact with it.
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('[AppointmentCard] Unknown appointment status:', status);
            }
            return 'Booked';
    }
}

/**
 * Glyph per status key — matches mockup §A (Day view) appointment cards.
 * Booked = •, Arrived = ✓, Started = ▶, Completed = ✓✓, NoShow = ✗,
 * Cancelled = — (em-dash, signals the strike-through state).
 *
 * AB#1901 — The default branch should never be reached now that
 * `statusToKey` always returns one of the 6 valid keys; keeping it as
 * a defensive neutral glyph (∙) for forward compatibility if a future
 * BE-side enum gets added before the FE updates.
 */
function glyphForStatusKey(key: CalendarStatusKey): string {
    switch (key) {
        case 'Booked': return '•';
        case 'Arrived': return '✓';
        case 'Started': return '▶';
        case 'Completed': return '✓✓';
        case 'NoShow': return '✗';
        case 'Cancelled': return '—';
        default: return '∙';
    }
}

/**
 * Mockup §A card style — translucent tinted background derived from the
 * status colour (alpha 0.18). Returns rgba(r,g,b,a). Defensive: if the
 * BE returns a non-#RRGGBB string, falls back to a neutral grey tint.
 */
function hexToRgba(hex: string, alpha: number): string {
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m) return `rgba(120, 120, 130, ${alpha})`;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Time string "HH:mm" → minutes since midnight. Returns 0 on malformed
 * input — the upstream BE guarantees the shape so this is mostly
 * defensive.
 */
function parseHHmm(t: string): number {
    if (!t || !t.includes(':')) return 0;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return h * 60 + m;
}

interface AppointmentCardProps {
    appointment: CalendarAppointmentDto;
    statuses: CalendarStatusDto[];
    dayStartMinutes: number;
    pixelsPerMinute: number;
    onOpen: (a: CalendarAppointmentDto) => void;
    /**
     * Layer 3 (audit Phase B-3) — when multiple appointments for the
     * same staff overlap in time, the parent runs a sweep-line lane
     * assignment that gives each overlapping card its 0-indexed lane
     * within the cluster and the total number of lanes in that
     * cluster. The card uses these to set `inset-inline-start` and
     * `width` so siblings render side-by-side at `1/laneCount` of
     * the column width instead of all stacking at full-width (which
     * silently buries every card except the last-painted one).
     *
     * Both default to 0/1 → full-width rendering (no overlap), so
     * callers without overlap detection get the legacy behaviour.
     */
    lane?: number;
    laneCount?: number;
    /** O1 (AB#2199, SO-09) — the deep-linked booking: outlined so the owner spots it. */
    highlighted?: boolean;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
    appointment, statuses, dayStartMinutes, pixelsPerMinute, onOpen,
    lane = 0, laneCount = 1, highlighted = false,
}) => {
    // AB#1797 (W2) — mark-paid modal state (feature-flagged)
    const [markPaidOpen, setMarkPaidOpen] = useState(false);
    const [idempotencyKey] = useState(() => generateIdempotencyKey());
    const startMin = parseHHmm(appointment.startTime);
    const endMin = parseHHmm(appointment.endTime);
    const top = Math.max(0, (startMin - dayStartMinutes) * pixelsPerMinute);
    const height = Math.max(20, (endMin - startMin) * pixelsPerMinute);

    const statusKey = statusToKey(appointment.status);
    const statusRow = statuses.find(s => s.statusKey === statusKey)
        // Defensive fallback if BE returns a status we didn't ship UI for.
        ?? statuses[0];

    const isCancelled = statusKey === 'Cancelled';
    // AB#2164 — Pending and Approved share the Booked palette row, so Pending
    // cards carry a distinct "awaiting confirmation" marker (Approved looks
    // exactly as before).
    const { t } = useLanguage();
    const isAwaitingConfirmation = parseAppointmentStatus(appointment.status) === AppointmentStatus.Pending;
    const awaitingLabel = isAwaitingConfirmation ? t('calendar.card.awaitingConfirmation') : null;

    const label = appointment.customerName ?? 'Walk-in';
    // AB#1849 (PBI-K) — Walk-in customer-gender glyph next to the name. Only
    // rendered when the BE serializes the value (legacy rows + Everyone-salon
    // bookings carry null). The textual aria-label below is the canonical
    // announcement for screen readers; the inline glyph is decorative
    // (aria-hidden) so the gender isn't double-announced.
    const customerGenderGlyph =
        appointment.customerGender === 'Male'
            ? '♂'
            : appointment.customerGender === 'Female'
                ? '♀'
                : null;
    const ariaLabel = [
        statusRow.labelEn,
        'appointment',
        '—',
        appointment.serviceName,
        'for',
        label,
        ...(appointment.customerGender ? ['(' + appointment.customerGender.toLowerCase() + ')'] : []),
        'at',
        appointment.startTime,
        ', ends',
        appointment.endTime,
        ...(awaitingLabel ? ['—', awaitingLabel] : []),
    ].join(' ');

    const glyph = glyphForStatusKey(statusKey);

    // Mockup §A — tinted background + left-edge stripe (3px solid status
    // colour). Foreground stays bright-on-dark for readability across any
    // owner-customised palette; mockup uses status-specific pastels but
    // those are baked in for the demo only — owner overrides in C8 can
    // produce any colour, so we keep fg a single token.
    const cardBg = hexToRgba(statusRow.colorHex, 0.18);
    const borderColor = statusRow.colorHex;

    return (
        <>
        <button
            type="button"
            className={`${s.card} ${isCancelled ? s.cardCancelled : ''} ${highlighted ? s.cardHighlighted : ''}`}
            data-highlighted={highlighted || undefined}
            style={{
                top: `${top}px`,
                height: `${height}px`,
                // Layer 3 — lane-based horizontal positioning. At
                // laneCount=1 (no overlap) the card fills the column
                // exactly as before. At laneCount>1 the card claims
                // its slice and siblings claim theirs.
                insetInlineStart: `${(lane / laneCount) * 100}%`,
                width: `calc(${100 / laneCount}% - 4px)`,
                background: cardBg,
                borderInlineStartColor: borderColor,
            }}
            aria-label={ariaLabel}
            title={[
                statusRow.labelEn,
                ...(awaitingLabel ? [awaitingLabel] : []),
                appointment.serviceName,
                label,
                ...(appointment.customerPhone ? [appointment.customerPhone] : []),
            ].join(' · ')}
            // AB#2149 — stop the click from bubbling to the staff-column
            // onClick that launches Quick book (CalendarGrid), so tapping a
            // booking opens ONLY the booking pop-up, not the new-booking dialog
            // on top of it. Mirrors WeekView + the absence blocks.
            onClick={(e) => { e.stopPropagation(); onOpen(appointment); }}
        >
            <span className={s.cardGlyph} style={{ color: borderColor }} aria-hidden="true">{glyph}</span>
            <div className={s.cardTime}>
                {/* O1 UAT — bidi-isolated so Arabic (RTL) reads 11:00–11:45, not 11:45–11:00. */}
                <bdi dir="ltr">{appointment.startTime}–{appointment.endTime}</bdi>
                {/* AB#2164 — Pending-only "awaiting confirmation" marker. The
                    button's aria-label carries the translated text, so the
                    visual badge is aria-hidden to avoid double announcement. */}
                {awaitingLabel && (
                    <span className={s.awaitingBadge} data-testid="awaiting-confirmation" aria-hidden="true">
                        ⏳
                    </span>
                )}
            </div>
            <div className={s.cardTitle}>
                {customerGenderGlyph && (
                    <span className={s.customerGenderGlyph} aria-hidden="true">
                        {customerGenderGlyph}
                    </span>
                )}
                {label}
            </div>
            <div className={s.cardSub}>{appointment.serviceName}</div>

            {/* AB#1797 (W2, AC-8) — Payment badge bottom-right corner.
                inset-inline-end keeps it at the visual end in RTL too.
                Only rendered when the feature flag is ON and the DTO
                carries a paymentStatus field (guards legacy responses). */}
            {APPOINTMENTS_LIST_V2 && appointment.paymentStatus != null && (
                <div className={s.paymentBadgeWrap}>
                    <PaymentBadge
                        variant={
                            appointment.paymentStatus === 'Paid' ? 'paid'
                            : appointment.paymentStatus === 'Refunded' ? 'refunded'
                            : 'unpaid' as PaymentBadgeVariant
                        }
                        size="sm"
                        // O1 (SO-07) — only when the backend says it can be paid
                        // (never on a pending request, a cancelled booking or a no-show).
                        onClick={appointment.canMarkPaid
                            ? () => setMarkPaidOpen(true)
                            : undefined}
                        ariaLabel={`${label} — ${appointment.paymentStatus}`}
                    />
                </div>
            )}
        </button>

        {/* AB#1797 (W2) — Mark-paid modal triggered from calendar card badge */}
        {APPOINTMENTS_LIST_V2 && markPaidOpen && (
            <MarkAsPaidModal
                appointmentId={appointment.id}
                customer={{ name: label }}
                service={{
                    name: appointment.serviceName,
                    amount: appointment.amount ?? 0,
                    currency: appointment.currencyIso ?? 'AED',
                }}
                staff={{ name: '', branch: '' }}
                isOpen={markPaidOpen}
                onClose={() => setMarkPaidOpen(false)}
                idempotencyKey={idempotencyKey}
                source="calendar"
                // AB#2034 — badge the mark-paid modal when this card is a bundle leg.
                bundle={appointment.bundleBookingId != null
                    ? { name: appointment.bundleName }
                    : undefined}
            />
        )}
        </>
    );
};

export default AppointmentCard;

/* ──────────────────────────── Peek popover ────────────────────────── */

interface AppointmentPeekPopoverProps {
    appointment: CalendarAppointmentDto;
    /** Kept for call-site compatibility; status words + actions no longer come from the palette. */
    statuses?: CalendarStatusDto[];
    onClose: () => void;
    /** O1 — staff column + branch the card sits in, shown in the booking detail. */
    staffName?: string | null;
    branchName?: string | null;
}

/** O1 (AB#2199) — Calendar card → shared booking detail model. */
export function calendarBookingModel(
    appointment: CalendarAppointmentDto,
    extra: { staffName?: string | null; branchName?: string | null } = {},
): BookingDetailModel {
    return {
        id: appointment.id,
        serviceName: appointment.serviceName,
        status: appointment.status,
        cancellationSource: appointment.cancellationSource,
        allowedTransitions: appointment.allowedTransitions,
        canMarkPaid: appointment.canMarkPaid,
        paymentStatus: appointment.paymentStatus,
        date: appointment.date,
        startTime: appointment.startTime,
        endTime: appointment.endTime,
        customerName: appointment.customerName,
        customerPhone: appointment.customerPhone,
        staffName: extra.staffName,
        branchName: extra.branchName,
        amount: appointment.amount,
        currencyIso: appointment.currencyIso,
        bundleName: appointment.bundleName,
        isBundle: appointment.bundleBookingId != null,
    };
}

/**
 * O1 (AB#2199, SO-06) — the Calendar booking detail. It is the SAME
 * BookingDetailDialog the Appointments list opens: status words from
 * lib/bookingLifecycle, actions = the backend's allowedTransitions (owner
 * state-machine endpoint, AB#2109; Decline / Cancel ask first, AB#2164), and
 * "Mark as paid" only when the backend says the booking can be paid (SO-07).
 */
export const AppointmentPeekPopover: React.FC<AppointmentPeekPopoverProps> = ({
    appointment, onClose, staffName, branchName,
}) => {
    const { t } = useLanguage();
    const [markPaidOpen, setMarkPaidOpen] = useState(false);
    const [idempotencyKey] = useState(() => generateIdempotencyKey());
    const label = appointment.customerName ?? t('booking.detail.walkIn');

    if (markPaidOpen) {
        return (
            <MarkAsPaidModal
                appointmentId={appointment.id}
                customer={{ name: label }}
                service={{
                    name: appointment.serviceName,
                    amount: appointment.amount ?? 0,
                    currency: appointment.currencyIso ?? 'AED',
                }}
                staff={{ name: staffName ?? '', branch: branchName ?? '' }}
                isOpen
                onClose={() => { setMarkPaidOpen(false); onClose(); }}
                idempotencyKey={idempotencyKey}
                source="calendar"
                bundle={appointment.bundleBookingId != null ? { name: appointment.bundleName } : undefined}
            />
        );
    }

    return (
        <BookingDetailDialog
            booking={calendarBookingModel(appointment, { staffName, branchName })}
            onClose={onClose}
            onMarkPaid={() => setMarkPaidOpen(true)}
        />
    );
};
